import React, { useState, useRef, useEffect } from 'react';
import { Send, Sparkles, HelpCircle, Mail, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Link, useNavigate } from 'react-router-dom';

const LiveSupport = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm Conchi, your dedicated Support Agent here at Constant Love. 💕\n\nI'm here to help you with:\n• Account and subscription questions\n• Feature guidance (Conchi AI, Love Garden, Divine Guidance, etc.)\n• Troubleshooting issues\n• Billing and payment questions\n• Any other questions about your self-love journey!\n\nWhat can I help you with today?",
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE_URL}/api/support/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          message: inputMessage,
          conversation_history: messages.slice(-10)
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      
      const assistantMessage = {
        role: 'assistant',
        content: data.response,
        timestamp: new Date(),
        helpLinks: data.help_links || []
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        role: 'assistant',
        content: "I apologize, but I'm having trouble connecting right now. Please try again in a moment, or email us at support@constantloveco.com for immediate assistance.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const quickActions = [
    { 
      icon: '💳',
      label: 'Upgrade to Growth Plan', 
      query: 'How do I upgrade to the Growth plan and what are the benefits?' 
    },
    { 
      icon: '❌',
      label: 'Cancel Subscription', 
      query: 'How do I cancel my subscription? Will I lose my data?' 
    },
    { 
      icon: '📱',
      label: 'Install App', 
      query: 'How do I install Constant Love on my iPhone/Android phone?' 
    },
    { 
      icon: '🔑',
      label: 'Account Issues', 
      query: 'I\'m having trouble logging in or accessing my account' 
    },
    {
      icon: '💬',
      label: 'About Conchi AI',
      query: 'Tell me more about Conchi and how she works'
    },
    {
      icon: '🎮',
      label: 'Love Garden Help',
      query: 'How do I play Love Garden and earn Love Score points?'
    },
  ];

  const handleQuickAction = (query) => {
    setInputMessage(query);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-rose-100">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-rose-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-gray-600 hover:text-rose-600 mb-3 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </button>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-rose-400 to-rose-600 rounded-xl flex items-center justify-center shadow-lg">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Live Support</h1>
                <p className="text-sm text-gray-600 flex items-center">
                  <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
                  Support by Conchi • Available 24/7
                </p>
              </div>
            </div>
            
            <div className="hidden sm:flex items-center space-x-4">
              <Link
                to="/help"
                className="flex items-center text-sm text-gray-600 hover:text-rose-600 transition-colors"
              >
                <HelpCircle className="w-4 h-4 mr-1" />
                Browse Help Center
              </Link>
              <a
                href="mailto:support@constantloveco.com"
                className="flex items-center text-sm text-gray-600 hover:text-rose-600 transition-colors"
              >
                <Mail className="w-4 h-4 mr-1" />
                Email Support
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Quick Actions */}
          <div className="lg:col-span-1">
            <Card className="p-4 bg-white/70 backdrop-blur-sm border-rose-200 sticky top-24">
              <h3 className="font-semibold text-gray-800 mb-3 text-sm">Quick Questions</h3>
              <div className="space-y-2">
                {quickActions.map((action, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleQuickAction(action.query)}
                    className="w-full text-left p-2 rounded-lg hover:bg-rose-50 transition-colors text-sm flex items-start space-x-2 group"
                  >
                    <span className="text-lg flex-shrink-0">{action.icon}</span>
                    <span className="text-gray-700 group-hover:text-rose-700">{action.label}</span>
                  </button>
                ))}
              </div>

              <div className="mt-4 pt-4 border-t border-rose-200">
                <p className="text-xs text-gray-600 mb-2">Need more help?</p>
                <div className="space-y-2">
                  <Link
                    to="/help"
                    className="block text-xs text-rose-600 hover:text-rose-800 underline"
                  >
                    📚 Browse Help Articles
                  </Link>
                  <a
                    href="mailto:support@constantloveco.com"
                    className="block text-xs text-rose-600 hover:text-rose-800 underline"
                  >
                    ✉️ Email Our Team
                  </a>
                </div>
              </div>
            </Card>
          </div>

          {/* Chat Area */}
          <div className="lg:col-span-3">
            <Card className="bg-white/70 backdrop-blur-sm border-rose-200 flex flex-col h-[calc(100vh-200px)]">
              {/* Messages */}
              <div 
                ref={chatContainerRef}
                className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4"
              >
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] sm:max-w-[75%] rounded-2xl p-4 ${
                        message.role === 'user'
                          ? 'bg-gradient-to-r from-rose-500 to-pink-600 text-white'
                          : 'bg-white border border-rose-200 text-gray-800 shadow-sm'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.content}</p>
                      {message.helpLinks && message.helpLinks.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-rose-200 space-y-2">
                          <p className="text-xs font-semibold text-rose-800">Related Help Articles:</p>
                          {message.helpLinks.map((link, idx) => (
                            <a
                              key={idx}
                              href={link.url}
                              className="block text-xs text-rose-600 hover:text-rose-800 underline"
                            >
                              📚 {link.title}
                            </a>
                          ))}
                        </div>
                      )}
                      <p className="text-xs opacity-70 mt-2">
                        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-white border border-rose-200 rounded-2xl p-4 shadow-sm">
                      <div className="flex space-x-2">
                        <div className="w-2 h-2 bg-rose-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-rose-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-rose-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="p-4 sm:p-6 border-t border-rose-200 bg-white/50">
                <div className="flex items-end space-x-3">
                  <div className="flex-1">
                    <textarea
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Type your question here..."
                      className="w-full resize-none border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 max-h-32"
                      rows="2"
                      disabled={isLoading}
                    />
                  </div>
                  <Button
                    onClick={handleSendMessage}
                    disabled={!inputMessage.trim() || isLoading}
                    className="bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white h-12 w-12 p-0 flex-shrink-0 rounded-lg"
                  >
                    <Send className="w-5 h-5" />
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-3 text-center">
                  🤖 Powered by AI • Responses are instant • For urgent issues, email support@constantloveco.com
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveSupport;
